import React, { useState } from 'react';
import { Mail, User, MapPin, Phone, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setIsSubmitted(true);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            address: '',
            phone: '',
            message: ''
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(
        err.response?.data?.detail || 
        'Something went wrong. Please try calling us directly at (437) 663-0763.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left Content */}
          <div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Need help or have questions? Send us a message and we'll get back to you as soon as possible.
            </p>
            
            <div className="bg-[#e6e67c] rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Why Email Us?</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">Get a quote for your specific situation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">Schedule a service appointment</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">Ask about our coverage areas</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-gray-900 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-800">General inquiries and support</span>
                </li>
              </ul>
              
              <div className="mt-8 pt-6 border-t border-gray-400/30">
                <p className="text-gray-800 font-semibold">
                  For immediate emergencies, please call us directly!
                </p>
              </div>
            </div>
          </div>

          {/* Right Content - Form */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-10 shadow-lg">
            {isSubmitted ? (
              <div data-testid="contact-form-success" className="text-center py-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-600">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Email Us</h3>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Error Alert */}
                  {error && (
                    <div data-testid="contact-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}
                  
                  {/* Name Field */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-900 font-medium flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-form-name"
                      className="bg-white border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    />
                  </div>

                  {/* Address Field */}
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-gray-900 font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Address *
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      required
                      placeholder="123 Main St, Toronto, ON"
                      value={formData.address}
                      onChange={handleChange}
                      data-testid="contact-form-address"
                      className="bg-white border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    />
                  </div>

                  {/* Phone Field */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-900 font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="(416) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      data-testid="contact-form-phone"
                      className="bg-white border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    />
                  </div>

                  {/* Message Field */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-gray-900 font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      How Can We Help? *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      placeholder="Please describe your situation or what service you need..."
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-form-message"
                      className="bg-white border-gray-300 focus:border-yellow-500 focus:ring-yellow-500 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="contact-form-submit"
                    className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
